/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { browserName, browserVersion, osName } from "react-device-detect";
import AppVersion from "../../package.json";
import type { IAuthData } from "@/context/auth-provider";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import qs from "querystring";
import { STORAGE, STORAGE_KEY } from "@/constants";
import { API_ENDPOINTS } from "./endpoints";
import { getItem, removeItem, setItem } from "@/lib/storage-utils";
import { getErrorMessage } from "@/lib/error-message-utils";
import { getOrCreateInstanceId } from "./instance-id";

// @ts-expect-error -- untyped module
const baseURL = import.meta.env.VITE_BASE_URL;

const EXCLUDED_ENDPOINTS_FROM_INTERCEPTORS_SET = new Set([
  API_ENDPOINTS.REFRESH,
  API_ENDPOINTS.LOGIN,
  API_ENDPOINTS.GROUP,
  // TODO: MAYBE NEED TO INCLUDE LOGS HERE(EXCLUDE)
]);

const PROD_BASE_URL = "https://api.zakipointhealth.com/api"; // temp change until middleware resolves EOB restriction to PROD only

const httpClient = axios.create({
  baseURL,
});

// ── Configuration Constants ──
const MAX_RETRY_ATTEMPTS = 1;

export const refreshAccessToken = async (authData: IAuthData) => {
  try {
    const url = API_ENDPOINTS.REFRESH;
    const params = {
      grant_type: "refresh_token",
      refresh_token: authData.user?.refresh_token,
    };
    const config = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };
    const response = await httpClient.post(url, qs.stringify(params), config);
    return response;
  } catch (e) {
    const axiosError = e as AxiosError;
    if (axiosError.response?.status === 400) {
      emitEvent(EventConstants.SESSION_EXPIRED);
    }
    throw e;
  }
};

// ── Request interceptor ──

httpClient.interceptors.request.use(
  (config) => {
    const storedAuthData =
      getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION) || null;

    if (storedAuthData?.user?.sid) {
      config.headers["sid"] = storedAuthData.user.sid;
    }

    // Allow caller-provided instanceId (e.g. replaying old log batches) to win.
    // If absent, attach current session instanceId.
    if (!config.headers["instanceId"]) {
      config.headers["instanceId"] = getOrCreateInstanceId();
    }

    const groupData = getItem(STORAGE_KEY.GROUP_DATA, STORAGE.SESSION);
    if (groupData) {
      config.headers["groupid"] = groupData?.group_id;
    }

    if (
      storedAuthData?.user?.access_token &&
      !EXCLUDED_ENDPOINTS_FROM_INTERCEPTORS_SET.has(config.url || "")
    ) {
      config.headers["Authorization"] =
        `Bearer ${storedAuthData?.user?.access_token}`;
    }

    config.headers.accept = "application/json";
    config.headers["platform"] = "Web";
    config.headers["browser"] = browserName;
    config.headers["browserversion"] = browserVersion;
    config.headers["os"] = osName;
    config.headers["appversion"] = AppVersion.version;
    config.headers["buildnumber"] = AppVersion.buildnumber;
    config.headers["source"] = "ZCONNECT";

    const userAgent = navigator.userAgent;
    const deviceType =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent,
      )
        ? "Mobile"
        : "Desktop";
    config.headers["devicedetails"] = deviceType;
    config.headers["Access-Control-Allow-Origin"] =
      baseURL ?? "https://zconnect-uat.zakipointhealth.com";

    config.timeout = 60000;

    if (config.url === API_ENDPOINTS.CARE_HISTORY.EOB) {
      config.baseURL = PROD_BASE_URL;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ── Queue for handling concurrent 401s ──

let isRefetching = false;
let failedQueue: Array<{ resolve: any; reject: any }> = [];

const processQueue = (
  error: any = null,
  token: string | null = null,
  sid: string | null = null,
) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve({ token, sid });
    }
  });
  failedQueue = [];
};

// ── Response interceptor ──

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };
    const errorData = error.response?.data as
      | { error?: string; message?: string }
      | undefined;

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (
      errorData?.error === "REQ-401" &&
      errorData?.message?.startsWith("Token") &&
      errorData?.message?.includes("has expired")
    ) {
      return Promise.reject(new Error(getErrorMessage(error)));
    }

    if (originalRequest.url === API_ENDPOINTS.CARE_HISTORY.EOB) {
      return Promise.reject(new Error(getErrorMessage(error)));
    }

    if (
      originalRequest._retry ||
      (originalRequest._retryCount ?? 0) >= MAX_RETRY_ATTEMPTS
    ) {
      emitEvent(EventConstants.SESSION_EXPIRED);
      removeItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
      removeItem(STORAGE_KEY.APP_INSTANCE_ID, STORAGE.SESSION);
      return Promise.reject(error);
    }

    const storedAuthData = getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
    const refreshToken = storedAuthData?.user?.refresh_token;

    if (!refreshToken) {
      emitEvent(EventConstants.SESSION_EXPIRED);
      removeItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
      removeItem(STORAGE_KEY.APP_INSTANCE_ID, STORAGE.SESSION);
      return Promise.reject(error);
    }

    if (isRefetching) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((data: any) => {
          const { token, sid } = data;

          if (!token) {
            throw new Error("No access token after refresh");
          }

          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers["Authorization"] = `Bearer ${token}`;

          if (sid) {
            originalRequest.headers["sid"] = sid;
          }

          originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1;

          return httpClient(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1;
    isRefetching = true;

    try {
      const params = {
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      };

      const config = {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      };

      const tokenRefreshResponse = await axios.post(
        `${baseURL}${API_ENDPOINTS.REFRESH}`,
        qs.stringify(params),
        config,
      );

      if (!tokenRefreshResponse.data?.access_token) {
        throw new Error("Invalid token refresh response");
      }

      const newAuthData = {
        ...storedAuthData,
        user: {
          ...storedAuthData?.user,
          ...tokenRefreshResponse?.data,
        },
      };

      setItem(STORAGE_KEY.AUTH_DATA, newAuthData, STORAGE.SESSION);

      const freshAccessToken = newAuthData.user.access_token;
      const freshSid = newAuthData.user.sid;

      httpClient.defaults.headers.common["Authorization"] =
        `Bearer ${freshAccessToken}`;
      if (freshSid) {
        httpClient.defaults.headers.common["sid"] = freshSid;
      }

      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers["Authorization"] = `Bearer ${freshAccessToken}`;
      if (freshSid) {
        originalRequest.headers["sid"] = freshSid;
      }

      processQueue(null, freshAccessToken, freshSid);

      return await httpClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null, null);

      emitEvent(EventConstants.SESSION_EXPIRED);
      removeItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
      removeItem(STORAGE_KEY.APP_INSTANCE_ID, STORAGE.SESSION);

      return Promise.reject(refreshError);
    } finally {
      isRefetching = false;
    }
  },
);

export default httpClient;
